import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Bell, Settings, LogOut, ArrowLeft, Menu, X, User, Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { motion, useScroll, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';

const Navigation = () => {
  const { darkMode } = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const [isOpen, setIsOpen] = useState(false);

  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const links = [
    { name: 'Home', href: '/' },
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Features', href: '/features' },
    { name: 'Profile', href: '/profile' },
    { name: 'About', href: '/about' },
    { name: 'Sign In', href: '/signin' }
  ];

  const LandingNavContent = () => (
    <div className="flex justify-between items-center">
      <Link to="/" className="flex items-center gap-2 text-[#EEF4E1]">
        <Shield className="w-6 h-6" />
        <span className="text-xl font-orbitron">Green Guard</span>
      </Link>
      
      <div className="flex items-center gap-8">
        {links.map((item) => (
          <motion.div
            key={item.name}
            whileHover={{ scale: 1.05 }}
            className="relative group"
          >
            <Link 
              to={item.href} 
              className="text-[#EEF4E1] font-medium tracking-wide
                hover:text-white transition-colors duration-300"
            >
              {item.name}
            </Link>
            <motion.div
              className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#DCE9C2]/50"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );

  const SubPageNavContent = () => (
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleBack}
          className="p-2 hover:bg-[#68A511]/20 rounded-full transition-all duration-300"
        >
          <ArrowLeft className="w-6 h-6 text-[#EEF4E1]" />
        </motion.button>
        <span className="text-xl font-orbitron text-[#EEF4E1] tracking-wide">
          {location.pathname.substring(1).charAt(0).toUpperCase() + location.pathname.slice(2)}
        </span>
      </div>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleSettings}
        className="p-2 hover:bg-[#68A511]/20 rounded-full transition-all duration-300"
      >
        <Settings className="w-6 h-6 text-[#EEF4E1]" />
      </motion.button>
    </div>
  );

  return (
    <>
      {/* Static Navigation */}
      <motion.nav
        initial={{ opacity: 1 }}
        animate={{ 
          opacity: isScrolled ? 0 : 1,
          y: isScrolled ? -100 : 0 
        }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-40 backdrop-blur-lg"
        style={{
          backgroundColor: darkMode 
            ? 'rgba(27, 67, 50, 0.3)' 
            : 'rgba(104, 165, 17, 0.3)',
          pointerEvents: isScrolled ? 'none' : 'auto',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="container mx-auto px-6 py-4">
          {isLandingPage ? <LandingNavContent /> : <SubPageNavContent />}
        </div>
      </motion.nav>

      {/* Floating Navigation */}
      <AnimatePresence>
        {isScrolled && (
          <motion.nav
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-4 w-full z-50"
          >
            <div className="container mx-auto px-4">
              <motion.div 
                className="mx-auto backdrop-blur-lg rounded-full px-6 py-4"
                style={{
                  backgroundColor: darkMode 
                    ? 'rgba(27, 67, 50, 0.3)' 
                    : 'rgba(104, 165, 17, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}
              >
                {isLandingPage ? <LandingNavContent /> : <SubPageNavContent />}
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Green Guard" className="w-8 h-8" />
              <span className="text-xl font-bold text-[#68A511]">Green Guard</span>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: isOpen ? 1 : 0, height: isOpen ? 'auto' : 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {links.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'bg-[#DCE9C2] text-[#68A511]'
                      : 'text-gray-600 hover:bg-[#DCE9C2] hover:text-[#68A511] dark:text-gray-300'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.nav>
    </>
  );
};

export default Navigation;
